import ClearIcon from "@mui/icons-material/Clear";
import DashboardIcon from "@mui/icons-material/Dashboard";
import FiberNewIcon from "@mui/icons-material/FiberNew";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import SearchIcon from "@mui/icons-material/Search";
import {
    Button,
    Chip,
    IconButton,
    InputAdornment,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { styled, useTheme } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";

import useCheckPermission from "../../../common/hooks/useCheckPermission";
import { toJalaliDate, toJalaliFullDate } from "../../../middleware/middleware";
import LoadingWrapper from "../loading";

/** گزینه‌های تعداد ردیف در صفحه‌بندی — همیشه صعودی */
const ROWS_PER_PAGE_OPTIONS = [10, 50, 100, 500];

function TableRowActions({ actions, row, mode = "inline" }) {
  const [anchorEl, setAnchorEl] = useState(null);

  const visibleActions = (Array.isArray(actions) ? actions : []).filter(
    (action) => !(typeof action.hidden === "function" && action.hidden(row))
  );

  if (visibleActions.length === 0) {
    return null;
  }

  const closeMenu = () => setAnchorEl(null);

  if (mode === "menu") {
    return (
      <>
        <Tooltip placement="top" title="عملیات">
          <span style={{ display: "inline-flex" }}>
            <IconButton
              size="small"
              color="primary"
              aria-label="عملیات"
              onClick={(event) => setAnchorEl(event.currentTarget)}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={closeMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          {visibleActions.map((action, actionIndex) => (
            <MenuItem
              key={`action-menu-${actionIndex}`}
              onClick={() => {
                closeMenu();
                action.onClick(row);
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>{action.icon}</ListItemIcon>
              <ListItemText primary={action.tooltip || "عملیات"} />
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  }

  return visibleActions.map((action, actionIndex) => {
    if (action.label) {
      return (
        <Button
          key={`action-${actionIndex}`}
          color={action.color || "primary"}
          size="small"
          variant={action.buttonVariant || "outlined"}
          startIcon={action.icon}
          onClick={() => action.onClick(row)}
          sx={{ ml: 0.5, whiteSpace: "nowrap" }}
        >
          {action.label}
        </Button>
      );
    }

    return (
      <Tooltip
        key={`action-${actionIndex}`}
        placement="top"
        title={action.tooltip}
      >
        <span style={{ display: "inline-flex" }}>
          <IconButton
            color={action.color}
            size="small"
            onClick={() => action.onClick(row)}
          >
            {action.icon}
          </IconButton>
        </span>
      </Tooltip>
    );
  });
}

TableRowActions.propTypes = {
  actions: PropTypes.array,
  row: PropTypes.object.isRequired,
  mode: PropTypes.oneOf(["inline", "menu"]),
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  border: 0,
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: 400,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
  },
  "&:first-of-type": {
    borderBottomLeftRadius: 10,
    borderTopLeftRadius: 10,
  },
  "&:last-child": {
    borderBottomRightRadius: 10,
    borderTopRightRadius: 10,
  },
}));

const StyledTableSortLabel = styled(TableSortLabel)(({ theme }) => ({
  "&:hover": {
    color: "#ccc",
  },
  "&.Mui-active": {
    color: "#fff",
    fontWeight: "bold",
    ".MuiTableSortLabel-icon": {
      color: "#fff",
    },
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  borderRadius: 10,
  border: 0,
  marginBottom: 16,
  whiteSpace: "nowrap",
  "&.MuiTableRow-hover:hover": {
    backgroundColor: "#C0D9FF",
  },
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.common.white,
    boxShadow: "0px 12px 21px 2px rgba(218, 230, 249, 0.4)",
  },
  "&:nth-of-type(odd)": {
    backgroundColor: "#e8f1ff",
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&.grid-status-danger,&.grid-status-error": {
    "td:first-of-type": {
      borderLeftColor: theme.palette.danger.main,
      borderLeftWidth: 6,
      borderLeftStyle: "solid",
    },
  },
  "&.grid-status-success": {
    "td:first-of-type": {
      borderLeftColor: theme.palette.success.main,
      borderLeftWidth: 6,
      borderLeftStyle: "solid",
    },
  },
  "&.grid-status-warning": {
    "td:first-of-type": {
      borderLeftColor: theme.palette.warning.main,
      borderLeftWidth: 6,
      borderLeftStyle: "solid",
    },
  },
  "&.grid-status-gray": {
    "td:first-of-type": {
      borderLeftColor: theme.palette.gray.main,
      borderLeftWidth: 6,
      borderLeftStyle: "solid",
    },
  },
  "&.grid-status-primary": {
    "td:first-of-type": {
      borderLeftColor: theme.palette.primary.main,
      borderLeftWidth: 6,
      borderLeftStyle: "solid",
    },
  },
}));

const reminderChipStyle = (color) => {
  switch (color) {
    case "error":
    case "danger":
      return { backgroundColor: "#FEE2E2", color: "#B91C1C" };
    case "warning":
      return { backgroundColor: "#FEF3C7", color: "#92400E" };
    case "success":
      return { backgroundColor: "#DCFCE7", color: "#166534" };
    case "primary":
      return { backgroundColor: "#DBEAFE", color: "#1D4ED8" };
    case "gray":
    default:
      return { backgroundColor: "#E5E7EB", color: "#374151" };
  }
};

function toSortableNumber(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (value == null || value === "") return null;
  const normalized = String(value)
    .replace(/[,\u066C\u060C\s]/g, "")
    .replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d))
    .replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d));
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

function descendingComparator(a, b, orderBy) {
  const aRaw = a[orderBy];
  const bRaw = b[orderBy];
  const aValue = orderBy === "row_index" ? parseInt(aRaw, 10) : aRaw;
  const bValue = orderBy === "row_index" ? parseInt(bRaw, 10) : bRaw;

  if (aValue == null && bValue == null) return 0;
  if (aValue == null) return 1;
  if (bValue == null) return -1;

  const aNum =
    typeof aValue === "number" && Number.isFinite(aValue)
      ? aValue
      : toSortableNumber(aValue);
  const bNum =
    typeof bValue === "number" && Number.isFinite(bValue)
      ? bValue
      : toSortableNumber(bValue);

  if (aNum != null && bNum != null) {
    return bNum - aNum;
  }

  const aText = String(aValue);
  const bText = String(bValue);

  if (bText < aText) return -1;
  if (bText > aText) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// Since 2020 all major browsers ensure sort stability with Array.prototype.sort().
// stableSort() brings sort stability to non-modern browsers (notably IE11). If you
// only support modern browsers you can replace stableSort(exampleArray, exampleComparator)
// with exampleArray.slice().sort(exampleComparator)
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort, headCells, actions, showIndex } =
    props;

  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <StyledTableRow>
        {showIndex ? (
          <StyledTableCell align={"center"}>
            <StyledTableSortLabel
              active={orderBy === "row_index"}
              direction={orderBy === "row_index" ? order : "asc"}
              onClick={createSortHandler("row_index")}
            >
              ردیف
              {orderBy === "row_index" ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </StyledTableSortLabel>
          </StyledTableCell>
        ) : null}

        {headCells.map((headCell) => (
          <StyledTableCell
            key={headCell.id}
            align={"center"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <StyledTableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </StyledTableSortLabel>
          </StyledTableCell>
        ))}
        {Array.isArray(actions) && actions.length > 0 ? (
          <StyledTableCell
            align={"center"}
            style={{
              position: "sticky",
              left: 0,
            }}
          >
            عملیات
          </StyledTableCell>
        ) : null}
      </StyledTableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])])
    .isRequired,
  rowCount: PropTypes.number.isRequired,
  headCells: PropTypes.any,
  actions: PropTypes.any,
};

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

export default function EnhancedTable({
  rowKey,
  rows,
  headCells,
  actions,
  pageSize = 50,
  showFilter = true,
  showIndex = true,
  loading = false,
  getRowStatus = undefined,
  showPaging = true,
  isDashboard = false,
  /** مرتب‌سازی اولیه، مثلاً { orderBy: "updateOn", order: "desc" } */
  defaultSort = null,
  /** حداقل عرض جدول؛ برای گرید فشرده مقدار "auto" بدهید */
  tableMinWidth = 750,
  /** نمایش عملیات: inline (آیکن‌ها) یا menu (منوی فشرده) */
  actionsMode = "inline",
  /** صفحه‌بندی سمت سرور — در این حالت برش/مرتب‌سازی سمت کلاینت انجام نمی‌شود */
  serverPagination = null,
}) {
  const getFallbackSort = () => {
    if (defaultSort?.orderBy) {
      return {
        orderBy: defaultSort.orderBy,
        order: defaultSort.order ?? "asc",
      };
    }

    const headCellIds = Array.isArray(headCells)
      ? headCells.map((cell) => cell?.id).filter(Boolean)
      : [];

    if (headCellIds.includes("updateOn")) {
      return { orderBy: "updateOn", order: "desc" };
    }

    if (headCellIds.includes("createOn")) {
      return { orderBy: "createOn", order: "desc" };
    }

    if (headCellIds.includes("id")) {
      return { orderBy: "id", order: "desc" };
    }

    // اگر ستون مناسب نبود، ترتیب دریافتی backend حفظ می‌شود.
    return { orderBy: null, order: "asc" };
  };

  const { orderBy: initialOrderBy, order: initialOrder } = getFallbackSort();

  const [rowsData, setRowsData] = useState(rows);
  const [order, setOrder] = useState(initialOrder);
  const [orderBy, setOrderBy] = useState(initialOrderBy);
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(pageSize);
  const [searched, setSearched] = useState("");

  useEffect(() => {
    if (!Array.isArray(rows)) {
      setRowsData([]);
      return;
    }

    // بدون جستجوی فعال، بلافاصله با props همگام شو
    // (افکت debounce قبلی با closure کهنهٔ rows=[] داده را بعد از ~500ms پاک می‌کرد)
    if (!searched || searched.trim() === "") {
      const newData = rows.map((x, index) => ({ row_index: index + 1, ...x }));
      setRowsData(newData);
    }
  }, [rows, searched]);

  // Debounce فقط وقتی کاربر واقعاً در جدول جستجو می‌کند
  useEffect(() => {
    if (!searched || searched.trim() === "") {
      return undefined;
    }

    const timeOutId = setTimeout(() => {
      const sourceRows = Array.isArray(rows) ? rows : [];
      const filteredRows = sourceRows
        .filter((row) => {
          let checked = false;
          for (let i = 0; i < headCells.length; i++) {
            const header = headCells[i];

            switch (header?.type) {
              case "date":
                break;
              case "boolean":
                let str = row[header.id] ? "فعال" : "غیرفعال";
                if (header.model) {
                  str = row[header.id]
                    ? header.model.active
                    : header.model.deActive;
                }
                checked = str.toLowerCase().includes(searched.toLowerCase());
                break;
              default:
                if (row[header.id])
                  checked = row[header.id]
                    .toString()
                    .toLowerCase()
                    .includes(searched.toLowerCase());
                break;
            }

            if (checked) break;
          }

          return checked;
        })
        .map((x, index) => ({ row_index: index + 1, ...x }));

      setRowsData(filteredRows);
    }, 500);

    return () => clearTimeout(timeOutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searched, rows]);

  const handleRequestSort = (event, property) => {
    const currentOrderBy = serverPagination?.orderBy ?? orderBy;
    const currentOrder = serverPagination?.order ?? order;
    const isAsc = currentOrderBy === property && currentOrder === "asc";
    const nextOrder = isAsc ? "desc" : "asc";

    if (serverPagination?.onSortChange) {
      serverPagination.onSortChange(property, nextOrder);
      return;
    }

    setOrder(nextOrder);
    setOrderBy(property);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const activeOrder = serverPagination?.order ?? order;
  const activeOrderBy = serverPagination?.orderBy ?? orderBy;
  const pagingCount = serverPagination?.totalCount ?? rowsData.length;
  const pagingPage = serverPagination?.page ?? page;
  const pagingRowsPerPage = serverPagination?.rowsPerPage ?? rowsPerPage;

  const handleChangePage = (event, newPage) => {
    if (serverPagination?.onPageChange) {
      serverPagination.onPageChange(newPage);
      return;
    }
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const next = parseInt(event.target.value, 10);
    if (serverPagination?.onRowsPerPageChange) {
      serverPagination.onRowsPerPageChange(next);
      return;
    }
    setRowsPerPage(next);
    setPage(0);
  };

  const sortedRows =
    serverPagination?.onSortChange
      ? rowsData
      : stableSort(rowsData, getComparator(activeOrder, activeOrderBy));

  const displayedRows = serverPagination
    ? sortedRows
    : sortedRows.slice(
        pagingPage * pagingRowsPerPage,
        pagingPage * pagingRowsPerPage + pagingRowsPerPage
      );

  const isSelected = (name) => selected.indexOf(name) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rowsData.length) : 0;

  const renderCellTemplate = (row, head) => {
    const item = row[head.id];
    switch (head.type) {
      case "number":
        return !Number.isNaN(item)
          ? Number.isInteger(item)
            ? item
            : item.toFixed(2)
          : "";

      case "date":
        return row[head.id] ? toJalaliDate(row[head.id]) : "";

      case "persiandatetime":
        return row[head.id] ? toJalaliFullDate(row[head.id]) : "";

      case "boolean":
        if (head.model) {
          return row[head.id] ? head.model.active : head.model.deActive;
        }
        return row[head.id] ? "فعال" : "غیرفعال";

      case "boolean-yesno":
        if (head.model) {
          return row[head.id] ? head.model.active : head.model.deActive;
        }
        return row[head.id] ? "بله" : "خیر";
      case "image":
        return row[head.id] ? (
          <img
            src={row[head.id]}
            alt="Preview"
            style={{
              width: "100%",
              height: "auto",
              maxWidth: "100px",
              maxHeight: "50px",
              objectFit: "contain",
            }}
          />
        ) : (
          ""
        );
      case "link":
        return row[head.id] ? (
          <a
            href={row[head.id]}
            target="_blank"
            rel="noopener noreferrer"
            title="دانلود" // Title added here
          >
            دانلود
          </a>
        ) : (
          ""
        );
      case "chip":
        if (!row[head.id]) return "-";
        return (
          <Chip
            label={row[head.id]}
            size="small"
            sx={reminderChipStyle(row?.[head?.model?.colorField])}
          />
        );

      default:
        if (typeof head.render === "function") {
          return head.render(row[head.id], row);
        }
        return row[head.id] ?? "-";
    }
  };

  const requestSearch = (searchedVal) => {
    setSearched(searchedVal);
  };

  const cancelSearch = () => {
    requestSearch("");
  };

  return (
    <Box sx={{ width: "100%" }}>
      {useCheckPermission("show") ? (
        <>
          {showFilter ? (
            <TextField
              value={searched}
              onChange={(e) => requestSearch(e.target.value)}
              size="small"
              variant="outlined"
              placeholder="جستجو..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searched && (
                  <IconButton size="small" onClick={() => cancelSearch()}>
                    <ClearIcon sx={{ width: 12, height: 12 }} />
                  </IconButton>
                ),
                style: {
                  paddingLeft: searched ? 2 : 10,
                },
              }}
              sx={{ mb: 1 }}
            />
          ) : null}

          {loading ? (
            <LoadingWrapper />
          ) : (
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table
                sx={{
                  minWidth: isDashboard ? 100 : tableMinWidth,
                  width: "100%",
                }}
                size={"small"}
                stickyHeader
              >
                <EnhancedTableHead
                  order={activeOrder}
                  orderBy={activeOrderBy}
                  onRequestSort={handleRequestSort}
                  rowCount={rowsData.length}
                  headCells={headCells}
                  actions={actions}
                  showIndex={showIndex}
                />
                <TableBody>
                  {Array.isArray(rowsData) && rowsData.length > 0 ? (
                    <>
                      {displayedRows.map((row, index) => {
                          const isItemSelected = isSelected(row[rowKey]);
                          // const labelId = `enhanced-table-checkbox-${index}`;

                          return (
                            <StyledTableRow
                              key={row[rowKey] || `row-${index}`}
                              hover
                              onClick={(event) => handleClick(event, row.name)}
                              role="checkbox"
                              aria-checked={isItemSelected}
                              tabIndex={-1}
                              selected={isItemSelected}
                              className={
                                getRowStatus
                                  ? `grid-status-${getRowStatus(row)}`
                                  : ""
                              }
                              dir="rtl"
                            >
                              {showIndex ? (
                                <StyledTableCell
                                  scope="row"
                                  align="center"
                                  sx={{ position: "relative" }}
                                >
                                  {row.row_index}

                                  {row.isNew ? (
                                    <FiberNewIcon
                                      color="primary"
                                      sx={{ position: "absolute", left: 2 }}
                                    />
                                  ) : null}
                                </StyledTableCell>
                              ) : null}

                              {headCells.map((head, index) => (
                                <StyledTableCell
                                  key={`StyledTableCell-${index}`}
                                  align="center"
                                >
                                  {renderCellTemplate(row, head)}
                                </StyledTableCell>
                              ))}

                              {Array.isArray(actions) && actions.length > 0 ? (
                                <StyledTableCell
                                  align={"center"}
                                  style={{
                                    position: "sticky",
                                    left: 0,
                                    background: "inherit",
                                  }}
                                >
                                  <TableRowActions
                                    actions={actions}
                                    row={row}
                                    mode={actionsMode}
                                  />
                                </StyledTableCell>
                              ) : null}
                            </StyledTableRow>
                          );
                        })}

                      {emptyRows > 0 && (
                        <TableRow
                          style={{
                            height: 30 * emptyRows,
                          }}
                        >
                          <TableCell colSpan={6} />
                        </TableRow>
                      )}
                    </>
                  ) : (
                    <TableRow
                      style={{
                        height: 30 * emptyRows,
                      }}
                    >
                      <TableCell colSpan={10}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            flexDirection: "column",
                            alignItems: "center",
                            mb: 4,
                          }}
                        >
                          <DashboardIcon
                            sx={{ fontSize: "8rem", my: 10 }}
                            color="gray"
                          />
                          <Typography
                            color="gray"
                            variant="h6"
                            fontWeight="bold"
                          >
                            داده ای جهت نمایش وجود ندارد
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {showPaging ? (
            <TablePagination
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              component="div"
              count={pagingCount}
              rowsPerPage={pagingRowsPerPage}
              page={pagingPage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="تعداد سطرهای هر صفحه:"
              lang="fa-IR"
              variant="outlined"
              shape="rounded"
              ActionsComponent={TablePaginationActions}
            />
          ) : null}
        </>
      ) : null}
    </Box>
  );
}
